import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef } from 'react';
import { coreApi } from '$api-clients';
import type { CreateRunnerBody, RunnerResponseDataFor } from './types';
import {
  getErrorCodeFromResponse,
  handleRunnerError,
  isAbortError,
  RunnerError,
} from './utils';

const DEFAULT_POLL_INTERVAL = 1000;

interface UseRunnerOptions {
  defaultErrorMessage: string;
  pollingInterval?: number;
  /**
   * Allow multiple runners in flight from the same hook instance (e.g.
   * downloading several documents at once). When false (default), a second
   * execute() while one is running rejects to guard against double-submits.
   */
  allowConcurrent?: boolean;
}

interface UseRunnerReturn {
  execute: <B extends CreateRunnerBody>(
    body: B,
  ) => Promise<RunnerResponseDataFor<B>>;
}

export function useRunner({
  defaultErrorMessage: errorMessage,
  pollingInterval = DEFAULT_POLL_INTERVAL,
  allowConcurrent = false,
}: UseRunnerOptions): UseRunnerReturn {
  const abortControllersRef = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const abortControllers = abortControllersRef.current;
    return () => {
      for (const controller of abortControllers) {
        controller.abort();
      }
      abortControllers.clear();
    };
  }, []);

  const execute = useCallback(
    async <B extends CreateRunnerBody>(
      body: B,
    ): Promise<RunnerResponseDataFor<B>> => {
      if (!allowConcurrent && abortControllersRef.current.size > 0) {
        throw new RunnerError('Runner is already executing');
      }

      const abortController = new AbortController();
      abortControllersRef.current.add(abortController);
      const { signal } = abortController;

      try {
        const createResponse = await coreApi.POST('/runners', { body, signal });

        if (createResponse.error) {
          throw new RunnerError(createResponse.error.message, {
            code: createResponse.error.code,
          });
        }

        const { id: runnerId } = createResponse.data;

        while (true) {
          // biome-ignore lint/performance/noAwaitInLoops: Sequential polling is intentional
          await new Promise((resolve) => setTimeout(resolve, pollingInterval));
          signal.throwIfAborted();

          const pollResponse = await coreApi.GET('/runners/{runnerId}', {
            params: { path: { runnerId } },
            signal,
          });

          if (!pollResponse.data) {
            if (pollResponse.error) {
              datadogRum.addError(pollResponse.error, { runnerId });
            }
            throw new RunnerError('Runner not found');
          }

          if (pollResponse.data.status === 'succeeded') {
            return pollResponse.data.responseData as RunnerResponseDataFor<B>;
          }

          if (pollResponse.data.status === 'failed') {
            const errorCode = getErrorCodeFromResponse(
              pollResponse.data.responseData,
            );
            throw new RunnerError('Runner failed', { code: errorCode });
          }
        }
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        handleRunnerError(error, errorMessage);
      } finally {
        abortControllersRef.current.delete(abortController);
      }
    },
    [pollingInterval, errorMessage, allowConcurrent],
  );

  return { execute };
}
